import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import {
  DollarSign,
  CheckCircle2,
  Clock,
  AlertCircle,
  Eye,
  Download,
  Search,
  Filter,
  Calendar,
  Users,
  Building2,
  Loader2,
  Save,
  X,
  Info,
} from "lucide-react";

function Comisiones({ constructora, isMobile }) {
  const [comisiones, setComisiones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEstado, setFilterEstado] = useState("pendientes");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProyecto, setFilterProyecto] = useState("");
  const [proyectos, setProyectos] = useState([]);
  const [proveedores, setProveedores] = useState([]);

  // Función simple para mostrar alertas
  const showAlert = (type, title, message) => {
    alert(`${title}: ${message}`);
  };

  // Configuración de vista
  const config = {
    gradient: "linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)",
    light: "#0ea5e910",
  };

  useEffect(() => {
    cargarDatos();
  }, [constructora.id]);

  useEffect(() => {
    cargarComisiones();
  }, [constructora.id, filterEstado, searchTerm, filterProyecto]);

  const cargarDatos = async () => {
    try {
      // Cargar proyectos
      const { data: proyectosData } = await supabase
        .from("proyectos")
        .select("id, codigo, apellido_cliente")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .is("deleted_at", null)
        .order("codigo");

      setProyectos(proyectosData || []);

      // Cargar proveedores
      const { data: proveedoresData } = await supabase
        .from("proveedores_servicios")
        .select("id, nombre")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .is("deleted_at", null)
        .order("nombre");

      setProveedores(proveedoresData || []);
    } catch (error) {
      console.error("Error cargando datos:", error);
    }
  };

  const cargarComisiones = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("comisiones")
        .select(`
          *,
          salidas (
            proyecto_id,
            monto,
            moneda,
            fecha,
            descripcion,
            proyectos (codigo, apellido_cliente)
          ),
          proveedores_servicios (nombre),
          entradas (id, monto, moneda, fecha)
        `)
        .eq("constructora_id", constructora.id);

      // Filtrar por estado
      if (filterEstado === "pendientes") {
        query = query.eq("estado", "pendiente");
      } else if (filterEstado === "cobradas") {
        query = query.eq("estado", "cobrada");
      }

      // Filtrar por proyecto
      if (filterProyecto) {
        query = query.eq("salidas.proyecto_id", filterProyecto);
      }

      // Filtrar por búsqueda
      if (searchTerm) {
        query = query.or(
          `proveedores_servicios.nombre.ilike.%${searchTerm}%,salidas.proyectos.codigo.ilike.%${searchTerm}%,salidas.proyectos.apellido_cliente.ilike.%${searchTerm}%`
        );
      }

      const { data, error } = await query.order("fecha_comision", { ascending: false });

      if (error) {
        console.error("Error cargando comisiones:", error);
      } else {
        setComisiones(data || []);
      }
    } catch (error) {
      console.error("Error:", error);
    }
    setLoading(false);
  };

  // Función para actualizar balance de caja para entrada de comisión
  const actualizarBalanceCajaComision = async (entradaOriginal = null, entradaNueva = null) => {
    try {
      // Si hay entrada original, revertir su impacto (restar del balance)
      if (entradaOriginal) {
        const { data: cajaOriginal, error: errorCajaOriginal } = await supabase
          .from("cajas")
          .select("balance_actual, nombre")
          .eq("id", entradaOriginal.caja_destino_id)
          .single();

        if (!errorCajaOriginal && cajaOriginal) {
          const balanceActual = parseFloat(cajaOriginal.balance_actual) || 0;
          const nuevoBalance = balanceActual - parseFloat(entradaOriginal.monto);

          await supabase
            .from("cajas")
            .update({
              balance_actual: nuevoBalance,
              updated_at: new Date().toISOString(),
            })
            .eq("id", entradaOriginal.caja_destino_id);

          console.log(`🔄 Balance revertido para ${cajaOriginal.nombre}: ${balanceActual} - ${entradaOriginal.monto} = ${nuevoBalance}`);
        }
      }

      // Si hay entrada nueva, aplicar su impacto (sumar al balance)
      if (entradaNueva) {
        const { data: cajaNueva, error: errorCajaNueva } = await supabase
          .from("cajas")
          .select("balance_actual, nombre")
          .eq("id", entradaNueva.caja_destino_id)
          .single();

        if (!errorCajaNueva && cajaNueva) {
          const balanceActual = parseFloat(cajaNueva.balance_actual) || 0;
          const nuevoBalance = balanceActual + parseFloat(entradaNueva.monto);

          await supabase
            .from("cajas")
            .update({
              balance_actual: nuevoBalance,
              updated_at: new Date().toISOString(),
            })
            .eq("id", entradaNueva.caja_destino_id);

          console.log(`✅ Balance actualizado para ${cajaNueva.nombre}: ${balanceActual} + ${entradaNueva.monto} = ${nuevoBalance}`);
        }
      }
    } catch (err) {
      console.error("Error en actualización de balance para comisión:", err);
    }
  };

  const marcarComoCobrada = async (comision) => {
    try {
      // Buscar caja de empresa de la moneda correspondiente
      const { data: cajaEmpresa } = await supabase
        .from("cajas")
        .select("id")
        .eq("constructora_id", constructora.id)
        .eq("tipo_caja", "empresa")
        .eq("moneda", comision.moneda)
        .eq("activo", true)
        .is("deleted_at", null)
        .single();

      if (!cajaEmpresa) {
        showAlert("error", "Error", "No se encontró caja de empresa para la moneda " + comision.moneda);
        return;
      }

      // Buscar concepto "Comisiones"
      const { data: conceptoComisiones } = await supabase
        .from("conceptos_entrada")
        .select("id")
        .eq("constructora_id", constructora.id)
        .eq("nombre", "Comisiones")
        .eq("activo", true)
        .single();

      if (!conceptoComisiones) {
        showAlert("error", "Error", "No se encontró el concepto 'Comisiones'");
        return;
      }

      // Crear entrada automática
      const { data: entrada, error: errorEntrada } = await supabase
        .from("entradas")
        .insert({
          constructora_id: constructora.id,
          proyecto_id: comision.salidas.proyecto_id,
          caja_destino_id: cajaEmpresa.id,
          concepto_id: conceptoComisiones.id,
          monto: comision.monto_comision,
          moneda: comision.moneda,
          tipo_cambio: 1, // Se puede ajustar según necesidad
          descripcion: `Comisión de ${comision.proveedores_servicios.nombre}`,
          fecha: new Date().toISOString().split("T")[0],
          created_by: constructora?.usuario_id,
        })
        .select()
        .single();

      if (errorEntrada) {
        console.error("Error creando entrada:", errorEntrada);
        showAlert("error", "Error", "Error al crear entrada: " + errorEntrada.message);
        return;
      }

      // Actualizar comisión como cobrada
      const { error: errorComision } = await supabase
        .from("comisiones")
        .update({
          estado: "cobrada",
          fecha_cobro: new Date().toISOString().split("T")[0],
          entrada_relacionada_id: entrada.id,
          updated_at: new Date().toISOString(),
        })
        .eq("id", comision.id);

      if (errorComision) {
        console.error("Error actualizando comisión:", errorComision);
        showAlert("error", "Error", "Error al actualizar comisión: " + errorComision.message);
        return;
      }

      // 🔧 ACTUALIZAR BALANCE DE LA CAJA DE EMPRESA
      await actualizarBalanceCajaComision(null, entrada);

      showAlert("success", "Éxito", "Comisión marcada como cobrada y entrada generada");
      cargarComisiones();
    } catch (error) {
      console.error("Error:", error);
      showAlert("error", "Error", "Error: " + error.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("T")[0].split("-");
    return `${day}/${month}/${year}`;
  };

  const formatMonto = (monto, moneda) => {
    if (!monto) return "";
    return `${moneda} $${parseFloat(monto).toLocaleString()}`;
  };

  const comisionesFiltradas = comisiones.filter(comision => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      comision.proveedores_servicios?.nombre?.toLowerCase().includes(searchLower) ||
      comision.salidas?.proyectos?.codigo?.toLowerCase().includes(searchLower) ||
      comision.salidas?.proyectos?.apellido_cliente?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div
      className="main-container"
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header */}
      <div
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: config.gradient,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px rgba(14, 165, 233, 0.25)",
              }}
            >
              <DollarSign size={20} color="white" />
            </div>
            <div>
              <h1
                style={{
                  margin: "0",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#1e293b",
                }}
              >
                Comisiones
              </h1>
              <p
                style={{
                  margin: "4px 0 0 0",
                  fontSize: "14px",
                  color: "#64748b",
                }}
              >
                Gestión de comisiones de proveedores
              </p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div
          style={{
            display: "grid",
            gap: isMobile ? "12px" : "16px",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
            marginBottom: "20px",
          }}
        >
          {/* Búsqueda */}
          <div style={{ position: "relative" }}>
            <Search
              size={16}
              color="#64748b"
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 1,
              }}
            />
            <input
              type="text"
              placeholder="Buscar por proveedor, proyecto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 12px 12px 36px",
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                fontSize: "14px",
                boxSizing: "border-box",
                outline: "none",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#0ea5e9";
                e.target.style.boxShadow = "0 0 0 3px rgba(14, 165, 233, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#f1f5f9";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Filtro de estado */}
          <select
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
            style={{
              padding: "12px 16px",
              border: "2px solid #f1f5f9",
              borderRadius: "12px",
              fontSize: "14px",
              background: "white",
              outline: "none",
              cursor: "pointer",
            }}
          >
            <option value="pendientes">Pendientes</option>
            <option value="cobradas">Cobradas</option>
            <option value="todas">Todas</option>
          </select>

          {/* Filtro de proyecto */}
          <select
            value={filterProyecto}
            onChange={(e) => setFilterProyecto(e.target.value)}
            style={{
              padding: "12px 16px",
              border: "2px solid #f1f5f9",
              borderRadius: "12px",
              fontSize: "14px",
              background: "white",
              outline: "none",
              cursor: "pointer",
            }}
          >
            <option value="">Todos los proyectos</option>
            {proyectos.map((proyecto) => (
              <option key={proyecto.id} value={proyecto.id}>
                {proyecto.codigo} - {proyecto.apellido_cliente}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista de comisiones */}
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "#64748b",
            }}
          >
            <Loader2 size={24} className="loading-pulse" />
            <p style={{ margin: "12px 0 0 0" }}>Cargando comisiones...</p>
          </div>
        ) : comisionesFiltradas.length === 0 ? (
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "#64748b",
            }}
          >
            <DollarSign size={48} color="#e2e8f0" />
            <p style={{ margin: "12px 0 0 0", fontSize: "16px" }}>
              No hay comisiones {filterEstado === "pendientes" ? "pendientes" : filterEstado === "cobradas" ? "cobradas" : ""}
            </p>
          </div>
        ) : (
          <div style={{ maxHeight: "600px", overflowY: "auto" }}>
            {comisionesFiltradas.map((comision) => (
              <div
                key={comision.id}
                style={{
                  padding: "16px",
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "4px",
                    }}
                  >
                    <Users size={14} color="#64748b" />
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      {comision.proveedores_servicios?.nombre}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                      }}
                    >
                      •
                    </span>
                    <Building2 size={14} color="#64748b" />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      {comision.salidas?.proyectos?.codigo} - {comision.salidas?.proyectos?.apellido_cliente}
                    </span>
                  </div>
                  
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      fontSize: "12px",
                      color: "#64748b",
                    }}
                  >
                    <span>
                      <Calendar size={12} style={{ marginRight: "4px" }} />
                      {formatDate(comision.fecha_comision)}
                    </span>
                    <span>
                      <DollarSign size={12} style={{ marginRight: "4px" }} />
                      {formatMonto(comision.monto_comision, comision.moneda)}
                    </span>
                    {comision.estado === "cobrada" && (
                      <span>
                        <CheckCircle2 size={12} style={{ marginRight: "4px" }} />
                        Cobrada {formatDate(comision.fecha_cobro)}
                      </span>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {comision.estado === "pendiente" ? (
                    <button
                      onClick={() => marcarComoCobrada(comision)}
                      style={{
                        padding: "8px 12px",
                        background: "#0ea5e9",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <CheckCircle2 size={12} />
                      Cobrar
                    </button>
                  ) : (
                    <span
                      style={{
                        padding: "4px 8px",
                        background: "#f0fdf4",
                        color: "#166534",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <CheckCircle2 size={10} />
                      Cobrada
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Comisiones;
